import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { roman, yearsForTrack } from "@/lib/school";

const EMPTY = {
  name: "", code: "", area_id: "", year_levels: [], academic_year: "",
  track_type: "residenziale", cfu: "", hours: "", description: "",
  is_seminar: false, order: 0, status: "attivo", teacher_ids: [],
};

export default function CourseFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting, forceSeminar = false, defaultTrack }) {
  const [form, setForm] = useState({ ...EMPTY, is_seminar: forceSeminar });
  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => (await api.get("/areas")).data,
    enabled: open,
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-course"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...EMPTY,
          ...initial,
          area_id: initial.area_id || "",
          year_levels: initial.year_levels?.length ? initial.year_levels : initial.year_level ? [initial.year_level] : [],
          academic_year: initial.academic_year || "",
          teacher_ids: initial.teacher_ids || [],
        });
      } else {
        setForm({ ...EMPTY, is_seminar: forceSeminar, track_type: defaultTrack || EMPTY.track_type });
      }
    }
  }, [open, initial, forceSeminar, defaultTrack]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleTeacher = (id) =>
    setForm((p) => ({
      ...p,
      teacher_ids: p.teacher_ids.includes(id) ? p.teacher_ids.filter((x) => x !== id) : [...p.teacher_ids, id],
    }));

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    payload.cfu = payload.cfu === "" ? null : parseFloat(payload.cfu);
    payload.hours = payload.hours === "" ? null : parseInt(payload.hours);
    payload.year_levels = [...(payload.year_levels || [])].map(Number).sort();
    payload.year_level = payload.year_levels[0] || null;
    payload.area_id = payload.area_id || null;
    payload.code = payload.code || null;
    payload.description = payload.description || null;
    payload.academic_year = payload.academic_year || null;
    onSubmit(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? (form.is_seminar ? "Nuovo seminario" : "Nuovo corso") : "Modifica corso"}</DialogTitle>
          <DialogDescription>Piano studi con CFU, ore, docenti co-titolari e area disciplinare.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs">Nome corso</Label>
            <Input data-testid="course-name" value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="es. Sistematica 1 – La Persona di Dio" />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Codice</Label>
            <Input value={form.code || ""} onChange={(e) => set("code", e.target.value)} placeholder="es. SIS1" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Area disciplinare</Label>
            <Select value={form.area_id || "__none"} onValueChange={(v) => set("area_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="course-area"><SelectValue placeholder="Nessuna" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessuna</SelectItem>
                {areas.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`course-area-opt-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Anni di corso (anche più di uno)</Label>
            <div className="flex gap-1.5 mt-1" data-testid="course-year">
              {yearsForTrack(form.track_type).map((y) => {
                const on = (form.year_levels || []).includes(y);
                return (
                  <button key={y} type="button" data-testid={`course-year-opt-${y}`}
                    onClick={() => set("year_levels", on ? form.year_levels.filter((x) => x !== y) : [...(form.year_levels || []), y])}
                    className={`flex-1 h-9 rounded-md text-xs border transition-colors ${on ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600"}`}>
                    {roman(y)}{form.track_type === "modulare" ? "" : " anno"}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Annualità</Label>
            <Input value={form.academic_year || ""} onChange={(e) => set("academic_year", e.target.value)} placeholder="2026/2027" />
          </div>

          <div className="col-span-1">
            <Label className="text-xs">Percorso</Label>
            <Select value={form.track_type || "residenziale"} onValueChange={(v) => setForm((p) => ({ ...p, track_type: v, year_levels: (p.year_levels || []).filter((y) => yearsForTrack(v).includes(y)) }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residenziale">Residenziale</SelectItem>
                <SelectItem value="modulare">Modulare</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">CFU</Label>
              <Input type="number" step="0.5" data-testid="course-cfu" value={form.cfu} onChange={(e) => set("cfu", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Ore</Label>
              <Input type="number" value={form.hours} onChange={(e) => set("hours", e.target.value)} />
            </div>
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <Checkbox
              id="is-seminar"
              data-testid="course-is-seminar"
              checked={!!form.is_seminar}
              onCheckedChange={(v) => set("is_seminar", !!v)}
            />
            <Label htmlFor="is-seminar" className="text-sm cursor-pointer">Questo è un seminario trasversale (1 CFU, aperto a tutti gli anni)</Label>
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Descrizione / programma</Label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Docenti co-titolari</Label>
            <div className="mt-1.5 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-stone-200 rounded-md bg-stone-50/50">
              {teachers.length === 0 ? (
                <span className="text-xs text-stone-500">Nessun docente. Creane uno dalla sezione Docenti.</span>
              ) : teachers.map((t) => {
                const active = form.teacher_ids.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTeacher(t.id)}
                    data-testid={`course-teacher-${t.id}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${active ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-200 hover:border-[#1C352D]"}`}
                  >
                    {t.first_name} {t.last_name}
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting} data-testid="course-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Crea corso" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
